import { Form, Formik } from "formik";
import { computed, observable } from "mobx";
import { inject, observer } from "mobx-react";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import React, { Component } from "react";
import { FormattedMessage, InjectedIntlProps, injectIntl } from "react-intl";
import { compose } from "recompose";
import * as yup from "yup";

import { PasswordField } from "nvent-web/components/form/PasswordField";
import { TextField } from "nvent-web/components/form/TextField";

import { CredentialsProps, RegistrationFlow } from "../../stores/RegistrationFlow";
import { RequiredFieldLabel, SubmitButton } from "./common";
import style from "./CredentialsForm.module.scss";

export interface CredentialsFormInnerProps extends InjectedIntlProps {
  registration: RegistrationFlow;
}

export class CredentialsFormInner extends Component<CredentialsFormInnerProps> {
  @observable
  submitPromise?: IPromiseBasedObservable<void>;

  @computed
  get validationSchema() {
    const { registration } = this.props;

    return yup.object({
      email: yup
        .string()
        .email()
        .required(),
      password: registration.isSigningUp
        ? yup
            .string()
            .required()
            .password()
        : yup.string(),
    });
  }

  render() {
    const { registration } = this.props;
    const { email, password } = registration;
    const initialValues: CredentialsProps = { email, password };
    const isSubmitting = this.submitPromise && this.submitPromise.state === "pending";

    return (
      <Formik initialValues={initialValues} onSubmit={this.submit} validationSchema={this.validationSchema}>
        {() => (
          <Form noValidate={true}>
            <TextField
              type="email"
              name="email"
              label={<FormattedMessage id="form.email" />}
              required
              disabled={!registration.isSigningUp}
            />

            {registration.isSigningUp && (
              <PasswordField name="password" label={<FormattedMessage id="form.password" />} required />
            )}

            <RequiredFieldLabel className={style.requiredFieldsLabel} />
            <SubmitButton disabled={isSubmitting} loading={isSubmitting}>
              <FormattedMessage id="actions.next" />
            </SubmitButton>
          </Form>
        )}
      </Formik>
    );
  }

  private submit = (values: any) => {
    const credentials = this.validationSchema.cast(values);
    this.submitPromise = fromPromise(this.props.registration.credentialsSubmitted(credentials));
  };
}

export const CredentialsForm = compose<CredentialsFormInnerProps, {}>(
  inject("registration"),
  injectIntl,
  observer
)(CredentialsFormInner);

export default CredentialsForm;
